/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useEffect } from 'react';
import { Modal, Radio, Space, Typography, Alert, Descriptions, message } from 'antd';
import type { WeatherGapInfo, WeatherExtendSource } from '../../types/simulation';
import { useExtendWeatherSeriesMutation } from '../../api/simulationApi';

interface WeatherGapDialogProps {
  open: boolean;
  gap: WeatherGapInfo | null;
  onClose: () => void;
  onExtended?: () => void;
}

const SOURCE_LABELS: Record<WeatherExtendSource, { title: string; hint: string }> = {
  nasa_power: {
    title: 'NASA POWER',
    hint: 'Реальные исторические данные NASA (доступно с 1981 г.). Рекомендуется для прошлых периодов.',
  },
  open_meteo: {
    title: 'Open-Meteo Archive',
    hint: 'Альтернативный архив (ERA5). Используйте, если NASA недоступен.',
  },
  synthetic_climatology: {
    title: 'Синтетическая климатология',
    hint: 'Сгенерировать по средним значениям из существующих данных фермы. Не требует интернета.',
  },
};

const DEFAULT_SUGGESTIONS: WeatherExtendSource[] = ['nasa_power', 'open_meteo', 'synthetic_climatology'];

const WeatherGapDialog: React.FC<WeatherGapDialogProps> = ({ open, gap, onClose, onExtended }) => {
  const [source, setSource] = useState<WeatherExtendSource>('nasa_power');
  const [extendWeatherSeries, { isLoading }] = useExtendWeatherSeriesMutation();

  useEffect(() => {
    if (open && gap?.suggestions?.length) {
      setSource(gap.suggestions[0]);
    }
  }, [open, gap?.suggestions]);

  const suggestions = useMemo<WeatherExtendSource[]>(
    () => (gap?.suggestions?.length ? gap.suggestions : DEFAULT_SUGGESTIONS),
    [gap?.suggestions],
  );
  const coordinates = gap?.coordinates && Number.isFinite(gap.coordinates.latitude) && Number.isFinite(gap.coordinates.longitude)
    ? gap.coordinates
    : null;

  if (!gap) return null;

  const handleConfirm = async () => {
    if (!gap.seriesId) {
      message.error('Не удалось определить ID погодной серии для расширения');
      return;
    }
    try {
      const result = await extendWeatherSeries({
        seriesId: gap.seriesId,
        startDate: gap.requestedStartDate,
        endDate: gap.requestedEndDate,
        source,
        coords: coordinates,
      }).unwrap();
      message.success(`Загружено ${result.pointCount} дней (${result.startDate} → ${result.endDate})`);
      onExtended?.();
      onClose();
    } catch (err: any) {
      const text = err?.data?.message || err?.message || 'Не удалось расширить серию';
      message.error(text);
    }
  };

  return (
    <Modal
      open={open}
      title="Догрузить недостающие погодные данные"
      onCancel={onClose}
      onOk={handleConfirm}
      okText="Загрузить"
      cancelText="Отмена"
      confirmLoading={isLoading}
      width={620}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        message="Погодная карта не покрывает запрашиваемый диапазон"
        description={gap.reason || 'Для построения графика нужно загрузить дополнительные точки погоды.'}
        style={{ marginBottom: 16 }}
      />

      <Descriptions size="small" column={1} bordered style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Серия">
          {gap.seriesName || gap.seriesId || '—'}
        </Descriptions.Item>
        <Descriptions.Item label="Не хватает диапазона">
          {gap.requestedStartDate} → {gap.requestedEndDate}
          {gap.missingDate ? ` (отсутствует с ${gap.missingDate})` : ''}
        </Descriptions.Item>
        {gap.seriesStartDate && gap.seriesEndDate && (
          <Descriptions.Item label="Текущие границы серии">
            {String(gap.seriesStartDate).slice(0, 10)} → {String(gap.seriesEndDate).slice(0, 10)}
          </Descriptions.Item>
        )}
        {coordinates && (
          <Descriptions.Item label="Координаты">
            {coordinates.latitude.toFixed(4)}, {coordinates.longitude.toFixed(4)}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Typography.Text strong>Источник данных:</Typography.Text>
      <Radio.Group
        value={source}
        onChange={(e) => setSource(e.target.value as WeatherExtendSource)}
        style={{ marginTop: 8, width: '100%' }}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          {suggestions.map((src) => {
            const meta = SOURCE_LABELS[src];
            if (!meta) return null;
            return (
              <Radio key={src} value={src}>
                <div>
                  <div><strong>{meta.title}</strong></div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{meta.hint}</Typography.Text>
                </div>
              </Radio>
            );
          })}
        </Space>
      </Radio.Group>
    </Modal>
  );
};

export default WeatherGapDialog;
